import { useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, GitCompareArrows, X, ZoomIn, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Reveal } from "@/components/Reveal";

const U = (id) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000`;
const FALLBACK = U("photo-1599707367072-cd6ada2bc375");

const STONES = [
  { id: 1, src: U("photo-1599707367072-cd6ada2bc375"), name: "Round Brilliant", tag: "White", color: "D · Colorless", carat: "2.41 ct", clarity: "VVS1", cut: "Ideal", origin: "Natural · GIA",
    blurb: "The benchmark of the trade — 58 facets tuned for maximum light return. A D-colour Ideal cut at this size is the stone every serious buyer measures the rest against.",
    pairs: ["Solitaire engagement ring", "Diamond stud earrings", "Tennis bracelet centrepiece"] },
  { id: 2, src: U("photo-1592136184798-ca0d8e17643a"), name: "Emerald Cut", tag: "White", color: "F · Colorless", carat: "1.94 ct", clarity: "VS2", cut: "Excellent", origin: "Natural · HRD",
    blurb: "The hall-of-mirrors cut. Step facets trade sparkle for a cool, architectural flash — unforgiving of inclusions, which is why we only stock clean examples.",
    pairs: ["Art-Deco inspired rings", "Three-stone settings", "Minimalist pendant"] },
  { id: 3, src: U("photo-1702149001693-67ca09997ecc"), name: "Solitaire, Black Mirror", tag: "White", color: "E · Colorless", carat: "3.02 ct", clarity: "VS1", cut: "Ideal", origin: "Natural · GIA",
    blurb: "Three carats of near-perfect white. Photographed on black mirror to show the kind of light discipline that separates a good stone from a great one.",
    pairs: ["High-jewellery solitaire", "Cocktail ring", "Legacy commission"] },
  { id: 4, src: U("photo-1748598057510-915898160cc0"), name: "Fancy Yellow Radiant", tag: "Yellow", color: "Fancy Intense Yellow", carat: "2.08 ct", clarity: "VS1", cut: "Excellent", origin: "Natural · GIA",
    blurb: "Radiant faceting concentrates the canary saturation beautifully. Fancy Intense is the sweet spot of the yellow market — vivid enough to turn heads, priced before Vivid premiums.",
    pairs: ["Halo ring with white melee", "Drop earrings", "Yellow-gold solitaire"] },
  { id: 5, src: U("photo-1731533621949-86a7b195884b"), name: "Yellow Melee Parcel", tag: "Yellow", color: "Fancy Yellow", carat: "4.20 ctw", clarity: "VS–SI", cut: "Very Good", origin: "Natural · GIA",
    blurb: "A matched parcel of fancy yellows, sorted for tone consistency — the difference between a pavé that glows evenly and one that looks patchy under light.",
    pairs: ["Pavé bands", "Halo surrounds", "Channel-set bracelets"] },
  { id: 6, src: U("photo-1679531751641-79f78cbb5c0b"), name: "Rose Pear", tag: "Pink", color: "Fancy Light Pink", carat: "1.12 ct", clarity: "VS1", cut: "Excellent", origin: "Lab-grown · IGI",
    blurb: "Natural pinks at this size command auction prices; this lab-grown twin delivers the same romance at a fraction of the position. Certified and disclosed, always.",
    pairs: ["Pear halo pendant", "Three-stone ring with white pears", "Rose-gold setting"] },
  { id: 7, src: U("photo-1705575463786-474f9271b1e1"), name: "Blush Cushion", tag: "Pink", color: "Fancy Pink", carat: "0.98 ct", clarity: "VS2", cut: "Excellent", origin: "Lab-grown · IGI",
    blurb: "A soft blush cushion with strong saturation through the face. Under one carat keeps it attainable — an easy yes for a first fancy-colour piece.",
    pairs: ["Rose-gold solitaire", "Bezel pendant", "Stacking ring centrepiece"] },
  { id: 8, src: U("photo-1664044020180-b75bfddf9776"), name: "Ice Blue Emerald", tag: "Blue", color: "Fancy Vivid Blue", carat: "1.55 ct", clarity: "VVS2", cut: "Excellent", origin: "Lab-grown · IGI",
    blurb: "Vivid blue is the rarest note in the fancy spectrum. Step-cut to let the colour breathe rather than shatter — a collector's stone at a trader's position.",
    pairs: ["Platinum statement ring", "Single-stone pendant", "Collector's vault"] },
  { id: 9, src: U("photo-1700317135098-f3e909bc1744"), name: "Sky Blue Oval", tag: "Blue", color: "Fancy Blue", carat: "1.31 ct", clarity: "VS1", cut: "Excellent", origin: "Lab-grown · IGI",
    blurb: "A lighter, airier blue with an elongated outline that flatters the hand. Ovals face up larger per carat than any other fancy shape.",
    pairs: ["East-west ring setting", "Drop earrings", "Delicate halo pendant"] },
  { id: 10, src: U("photo-1724149825757-960096bc15ba"), name: "Noir Marquise", tag: "Black", color: "Fancy Black", carat: "2.77 ct", clarity: "Opaque", cut: "Very Good", origin: "Natural · HRD",
    blurb: "Black diamonds trade on attitude, not brilliance — a liquid-metal lustre that reads modern and masculine. Marquise points sharpen the drama.",
    pairs: ["Men's signet ring", "Avant-garde cocktail ring", "Contrast pavé with whites"] },
  { id: 11, src: U("photo-1731533622011-155f51dfe8bf"), name: "Violet Princess Suite", tag: "Purple", color: "Fancy Purple", carat: "3.10 ctw", clarity: "VS", cut: "Excellent", origin: "Lab-grown · IGI",
    blurb: "A matched suite of violet princess cuts, graded together for hue and tone. Rare enough that most jewellers have never set one.",
    pairs: ["Matched stud earrings", "Cluster cocktail ring", "Graduated bracelet"] },
  { id: 12, src: U("photo-1771792880186-a2ed14b120ef"), name: "Calibrated Melee", tag: "White", color: "F/G · Colorless", carat: "0.30 — 0.50 ct", clarity: "VS", cut: "Excellent", origin: "Natural · GIA",
    blurb: "The unsung hero of fine jewellery. Tightly calibrated, bright white melee is what makes pavé read as a river of light instead of grey dust.",
    pairs: ["Pavé settings", "Wedding bands", "Watch dials and bezels"] },
];

const FILTERS = ["All", "White", "Yellow", "Pink", "Blue", "Black", "Purple"];

const StoneImg = ({ src, alt, className }) => {
  const [s, setS] = useState(src);
  return <img src={s} alt={alt} onError={() => s !== FALLBACK && setS(FALLBACK)} loading="lazy" className={className} />;
};

const SpecRows = ({ s }) => (
  <div className="mt-4 space-y-2 text-sm">
    {[["Color", s.color], ["Carat", s.carat], ["Clarity", s.clarity], ["Cut", s.cut], ["Origin", s.origin]].map(([k, v]) => (
      <div key={k} className="flex justify-between gap-4 border-b border-white/5 pb-2">
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#717680]">{k}</span>
        <span className="text-right text-[#E8DCC0]">{v}</span>
      </div>
    ))}
  </div>
);

const Lightbox = ({ stone, onClose, onPrev, onNext }) => {
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const ref = useRef(null);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };

  if (!stone) return null;
  return (
    <motion.div
      data-testid="gallery-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0E0E10]/95 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <button data-testid="lightbox-close" onClick={onClose} className="glass-btn absolute right-6 top-6 !rounded-full !p-3" aria-label="Close">
        <X className="h-4 w-4" />
      </button>
      <button
        data-testid="lightbox-prev"
        onClick={(e) => { e.stopPropagation(); setZoom(false); onPrev(); }}
        className="glass-btn absolute left-4 top-1/2 !rounded-full !p-3 lg:left-10"
        aria-label="Previous"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        data-testid="lightbox-next"
        onClick={(e) => { e.stopPropagation(); setZoom(false); onNext(); }}
        className="glass-btn absolute right-4 top-1/2 !rounded-full !p-3 lg:right-10"
        aria-label="Next"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      <div className="grid w-full max-w-5xl items-center gap-6 md:grid-cols-[1.15fr_1fr]" onClick={(e) => e.stopPropagation()}>
        <div>
          <div
            ref={ref}
            onMouseMove={onMove}
            onClick={() => setZoom((z) => !z)}
            className="cursor-zoom-in overflow-hidden rounded-2xl border border-white/10"
            data-testid="lightbox-image-wrap"
          >
            <StoneImg
              src={stone.src}
              alt={stone.name}
              className="max-h-[70vh] w-full object-cover transition-transform duration-500"
            />
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-[11px] text-[#717680]">
            <ZoomIn className="h-3.5 w-3.5" /> {zoom ? "Click to reset" : "Click to zoom · move to inspect"}
          </p>
        </div>

        <div className="glass-panel rounded-2xl p-6 lg:p-8" data-testid="lightbox-detail">
          <p className="eyebrow">{stone.tag} diamond</p>
          <h3 className="font-display mt-3 text-3xl font-light text-white">{stone.name}</h3>
          <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[#B8BCC2]">
            {stone.color} · {stone.carat} · {stone.clarity} · {stone.cut} · {stone.origin}
          </p>
          <p className="mt-5 text-sm leading-relaxed text-[#B8BCC2]">{stone.blurb}</p>

          <p className="mt-6 text-[11px] uppercase tracking-[0.25em] text-[#717680]">Best worn as</p>
          <ul className="mt-3 space-y-2" data-testid="lightbox-pairs">
            {stone.pairs.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-sm text-[#E8DCC0]">
                <span className="text-[8px] text-[#C9A24B]">◆</span> {p}
              </li>
            ))}
          </ul>

          <Link
            to="/contact"
            onClick={onClose}
            data-testid="lightbox-enquire-btn"
            className="gold-btn mt-7 w-full !py-3 text-xs"
          >
            Enquire About This Stone <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <p className="mt-3 text-center text-[10px] uppercase tracking-[0.15em] text-[#717680]">
            Prices on application — firm quote from the desk
          </p>
        </div>
      </div>
      <style>{`[data-testid="lightbox-image-wrap"] img { transform-origin: ${origin}; transform: scale(${zoom ? 2.4 : 1}); }`}</style>
    </motion.div>
  );
};

export const GalleryPage = () => {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [compare, setCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const stones = useMemo(
    () => (filter === "All" ? STONES : STONES.filter((s) => s.tag === filter)),
    [filter]
  );

  const toggleCompare = (id) =>
    setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id) : c.length < 2 ? [...c, id] : [c[1], id]));

  const compareStones = compare.map((id) => STONES.find((s) => s.id === id)).filter(Boolean);

  return (
    <div className="pt-24" data-testid="gallery-page">
      <header className="mx-auto max-w-7xl px-6 pb-10 pt-16 lg:px-12">
        <Reveal>
          <p className="eyebrow mb-4">Gallery</p>
          <h1 className="font-display max-w-3xl text-4xl font-light leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Whites, fancies & <span className="gold-text italic">statements</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8BCC2] sm:text-lg">
            A curated look at the goods we trade. Click any stone to inspect it under the light —
            or mark two and compare them side by side.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="gallery-filters">
            {FILTERS.map((f) => (
              <button key={f} data-testid={`gallery-filter-${f.toLowerCase()}`} data-active={filter === f} onClick={() => setFilter(f)} className="chip">
                {f}
              </button>
            ))}
          </div>
        </Reveal>
      </header>

      <section className="mx-auto max-w-7xl px-6 pb-32 lg:px-12">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
          {stones.map((s, i) => {
            const inCompare = compare.includes(s.id);
            return (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`gallery-stone-${s.id}`}
                  className={`group relative aspect-[4/3] overflow-hidden rounded-2xl border transition-all duration-500 ${
                    inCompare ? "border-[#C9A24B] shadow-[0_0_30px_rgba(201,162,75,0.25)]" : "border-white/10 hover:border-[#C9A24B]/40"
                  }`}
                >
                  <button onClick={() => setLightbox(s.id)} className="absolute inset-0" data-testid={`gallery-open-${s.id}`} aria-label={`View ${s.name}`}>
                    <StoneImg src={s.src} alt={s.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </button>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0E0E10]/95 to-transparent p-5 pt-14">
                    <div className="flex items-end justify-between gap-3">
                      <div>
                        <h3 className="font-display text-lg text-white">{s.name}</h3>
                        <p className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-[#B8BCC2]">
                          {s.color} · {s.carat}
                        </p>
                      </div>
                      <button
                        data-testid={`gallery-compare-${s.id}`}
                        onClick={() => toggleCompare(s.id)}
                        className={`pointer-events-auto flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-wider transition-all duration-300 ${
                          inCompare
                            ? "border-[#C9A24B] bg-[#C9A24B] text-[#0E0E10]"
                            : "border-white/20 bg-black/40 text-[#B8BCC2] backdrop-blur-md hover:border-[#C9A24B]/60 hover:text-white"
                        }`}
                      >
                        <GitCompareArrows className="h-3 w-3" />
                        {inCompare ? "Selected" : "Compare"}
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-10 text-center text-[11px] uppercase tracking-[0.2em] text-[#717680]" data-testid="gallery-note">
            Illustrative photography — client stone media added at launch · Prices on application only
          </p>
        </Reveal>
      </section>

      <AnimatePresence>
        {lightbox && (
          <Lightbox
            stone={STONES.find((s) => s.id === lightbox)}
            onClose={() => setLightbox(null)}
            onPrev={() => setLightbox((id) => STONES[(STONES.findIndex((s) => s.id === id) - 1 + STONES.length) % STONES.length].id)}
            onNext={() => setLightbox((id) => STONES[(STONES.findIndex((s) => s.id === id) + 1) % STONES.length].id)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {compare.length > 0 && (
          <motion.div
            data-testid="compare-bar"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            className="glass-panel fixed bottom-6 left-1/2 z-[80] flex -translate-x-1/2 items-center gap-4 rounded-full px-5 py-3"
          >
            <div className="flex -space-x-3">
              {compareStones.map((s) => (
                <StoneImg key={s.id} src={s.src} alt={s.name} className="h-10 w-10 rounded-full border-2 border-[#0E0E10] object-cover" />
              ))}
            </div>
            <span className="text-xs text-[#B8BCC2]">{compare.length}/2 selected</span>
            <button
              data-testid="compare-open-btn"
              disabled={compare.length < 2}
              onClick={() => setCompareOpen(true)}
              className="gold-btn !px-5 !py-2 text-xs disabled:opacity-40"
            >
              Compare
            </button>
            <button data-testid="compare-clear-btn" onClick={() => setCompare([])} className="text-xs text-[#717680] hover:text-white">
              Clear
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent data-testid="compare-modal" className="glass-panel max-w-3xl border-white/15 bg-[#141418]/95 text-white sm:rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-light text-white">
              Side by <span className="gold-text italic">side</span>
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 sm:grid-cols-2">
            {compareStones.map((s) => (
              <div key={s.id} data-testid={`compare-stone-${s.id}`}>
                <div className="overflow-hidden rounded-xl border border-white/10">
                  <StoneImg src={s.src} alt={s.name} className="aspect-square w-full object-cover" />
                </div>
                <h3 className="font-display mt-4 text-xl text-white">{s.name}</h3>
                <SpecRows s={s} />
              </div>
            ))}
          </div>
          <p className="mt-4 text-center text-[11px] uppercase tracking-[0.2em] text-[#717680]">
            Prices on application — request a firm quote from the desk
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};
